import React, { useEffect, useRef, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProductionAssignmentBoard from './ProductionAssignmentBoard';
import EventActivityTimeline from './EventActivityTimeline';
import EventEditModal from './EventEditModal';
import VenueLogisticsAlerts from './VenueLogisticsAlerts';
import ClientAnswersPanel from './ClientAnswersPanel';
import { printEventSection } from '../utils/printEvent';
import { addEventActivity, getEventActivity } from '../utils/eventActivityStorage';
import { saveEventTemplate } from '../utils/templateStorage';
import { formatEventDate, paymentStatus } from '../utils/eventDisplay';
import './event-detail.css';
import './client-answers.css';
import './event-detail-refinements.css';

const {
  FiArrowLeft,
  FiBookmark,
  FiCheckCircle,
  FiCopy,
  FiDownload,
  FiEdit3,
  FiFileText,
  FiMail,
  FiPrinter,
  FiTrash2
} = FiIcons;

const tabTitles = {
  overview: 'Event Summary',
  production: 'DJ Production Sheet',
  assignments: 'Production Assignments',
  notes: 'Internal Notes'
};

function hasValue(value) {
  return value !== undefined && value !== null &&
    String(value).trim() !== '' && String(value) !== '—';
}

function DetailTable({ rows }) {
  const visibleRows = rows.filter(([, value]) => hasValue(value));
  if (!visibleRows.length) return null;

  return (
    <div className="detail-table-wrap">
      <table className="detail-table">
        <tbody>
          {visibleRows.map(([label, value]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EventDetail({
  event,
  onBack,
  onUpdateEvent,
  onDeleteEvent,
  createClientLink,
  onOpenQuestionnaire
}) {
  const [notes, setNotes] = useState('');
  const [tab, setTab] = useState('overview');
  const [linkCopied, setLinkCopied] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [activities, setActivities] = useState(() => getEventActivity(event.id, event));
  const printContentRef = useRef(null);
  const services = Array.isArray(event.services)
    ? event.services.filter(hasValue)
    : [];
  const payment = paymentStatus(event);
  const isWooEvent = String(event.id).startsWith('woo-');

  useEffect(() => {
    setActivities(getEventActivity(event.id, event));
  }, [event.id, event]);

  const recordActivity = (type, title, description) => {
    const activity = addEventActivity(event.id, { type, title, description });
    setActivities((current) => [activity, ...current]);
  };

  const copyClientLink = async () => {
    const link = createClientLink(event);
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      setActionMessage('Client planner link copied.');
      recordActivity('copied', 'Client link copied', 'The client planner link was copied.');
      window.setTimeout(() => setLinkCopied(false), 2200);
    } catch {
      window.prompt('Copy this client planner link:', link);
    }
  };

  const sendQuestionnaire = async () => {
    const link = createClientLink(event);
    try {
      await navigator.clipboard.writeText(link);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2200);
    } catch {
      // Keep the link available in the email draft if clipboard access is blocked.
    }

    if (!event.email) {
      setActionMessage('Add a client email to this event before opening an email draft.');
      return;
    }

    const subject = `Your ${event.name || 'event'} questionnaire`;
    const body = `Hi ${event.client || 'there'},\n\nPlease complete your event questionnaire using this link:\n\n${link}\n\nThank you!`;
    window.location.href = `mailto:${event.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setActionMessage('The client link was copied and an email draft was opened.');
    recordActivity('update', 'Questionnaire email drafted', `A questionnaire link was prepared for ${event.client || 'the client'}.`);
  };

  const saveEvent = (updatedEvent) => {
    onUpdateEvent(updatedEvent);
    setShowEdit(false);
    setActionMessage('Event details saved successfully.');
    recordActivity('edited', 'Event details updated', 'Event information was edited and saved.');
  };

  const deleteEvent = () => {
    if (window.confirm(`Delete "${event.name}"? This action cannot be undone.`)) {
      onDeleteEvent(event.id);
    }
  };

  const saveTemplate = () => {
    saveEventTemplate(event);
    setActionMessage(`${event.name || 'Event'} was saved as a template in Templates.`);
    recordActivity('template', 'Saved as template', `${event.name || 'This event'} was saved to the Templates tab.`);
  };

  const saveNotes = () => {
    recordActivity('notes', 'Internal notes saved', 'Production notes were updated for this event.');
    setActionMessage('Internal notes saved successfully.');
  };

  const printCurrentTab = () => {
    const opened = printEventSection(
      printContentRef.current,
      `${event.name || 'Event'} — ${tabTitles[tab]}`
    );
    setActionMessage(opened
      ? 'Print view opened. Choose Print or Save as PDF in the browser dialog.'
      : 'The print window was blocked. Allow pop-ups for this site and try again.');
  };

  const eventRows = [
    ['Client', event.client],
    ['Event date', formatEventDate(event.date)],
    ['Event type', event.type],
    ['Venue', event.venue],
    ['Guest count', event.guests],
    ['Assigned DJ', event.dj],
    ['Client email', event.email],
    ['Client phone', event.phone],
    ['WooCommerce order', event.wooOrderNumber
      ? event.wooAdminUrl
        ? <a href={event.wooAdminUrl} target="_blank" rel="noreferrer">#{event.wooOrderNumber}</a>
        : `#${event.wooOrderNumber}`
      : ''],
    ['Payment status', payment?.label],
    ['Order total', event.wooTotal]
  ];

  const progressSteps = [
    { label: 'Booked', complete: true },
    { label: 'Questionnaire sent', complete: Boolean(event.shareToken) },
    { label: 'Client answered', complete: Number(event.completion) > 0 },
    { label: 'Approved', complete: event.status === 'Approved' },
    { label: 'Event done', complete: event.status === 'Completed' }
  ];

  const detailRows = isWooEvent
    ? [
      ['Event date', formatEventDate(event.date)],
      ['Event duration', event.eventDuration],
      ['Event start time', event.eventStartTime],
      ['Setup start time', event.setupStartTime],
      ['Event end time', event.eventEndTime],
      ['Venue', event.venue],
      ['Guest count', event.guests]
    ]
    : [
      ['Event date', formatEventDate(event.date)],
      ['Event duration', event.eventDuration],
      ['Event start time', event.eventStartTime],
      ['Setup start time', event.setupStartTime],
      ['Event end time', event.eventEndTime],
      ['Venue', event.venue],
      ['Guest count', event.guests]
    ];

  const logisticsRows = event.serviceType === 'Self pickup and return'
    ? [
      ['Pickup', [formatEventDate(event.date), event.eventStartTime].filter(Boolean).join(' · ')],
      ['Return', [formatEventDate(event.returnDate), event.returnTime].filter(Boolean).join(' · ')],
      ['Pickup address', event.pickupAddress]
    ]
    : detailRows;

  return (
    <main className="main-content event-detail">
      <button type="button" className="back-link" onClick={onBack}>
        <SafeIcon icon={FiArrowLeft} /> Back to events
      </button>

      <div className="event-detail-hero">
        <div>
          {payment && <span className={`payment-badge ${payment.tone}`}>{payment.label}</span>}
          <h1>{event.name}</h1>
          <p>{formatEventDate(event.date)} · {event.venue || 'Venue pending'}</p>
        </div>
        <div className="event-actions">
          <button type="button" className="button primary main-event-action" onClick={sendQuestionnaire}>
            <SafeIcon icon={FiMail} />
            {event.completion > 0 ? 'Review client answers' : 'Send questionnaire to client'}
          </button>
          <button type="button" className="button secondary" onClick={() => onOpenQuestionnaire?.()}>
            <SafeIcon icon={FiFileText} /> Edit questionnaire
          </button>
          <button type="button" className="button secondary" onClick={() => setShowEdit(true)}>
            <SafeIcon icon={FiEdit3} /> Edit event
          </button>
          <button type="button" className="button secondary" onClick={copyClientLink}>
            <SafeIcon icon={FiCopy} /> {linkCopied ? 'Link copied' : 'Copy client link'}
          </button>
          <button type="button" className="button secondary" onClick={saveTemplate}>
            <SafeIcon icon={FiBookmark} /> Save as template
          </button>
          <button type="button" className="button danger-button" onClick={deleteEvent}>
            <SafeIcon icon={FiTrash2} /> Delete event
          </button>
        </div>
      </div>

      {actionMessage && <div className="event-action-message" role="status">{actionMessage}</div>}

      {event.wooOrderNumber && (
        <p className="woocommerce-note">
          From WooCommerce order{' '}
          {event.wooAdminUrl
            ? <a href={event.wooAdminUrl} target="_blank" rel="noreferrer">#{event.wooOrderNumber}</a>
            : `#${event.wooOrderNumber}`}
          {isWooEvent && '. Edit booking details in WooCommerce.'}
        </p>
      )}

      <section className="detail-facts" aria-label="Event overview">
        <div><small>Client</small><strong>{event.client || '—'}</strong></div>
        <div><small>Event date</small><strong>{formatEventDate(event.date)}</strong></div>
        <div><small>Venue</small><strong>{event.venue || '—'}</strong></div>
        <div><small>Phone</small><strong>{event.phone || '—'}</strong></div>
        <div><small>Email</small><strong>{event.email || '—'}</strong></div>
        <div><small>DJ</small><strong>{event.dj || 'TBD'}</strong></div>
      </section>

      <section className="event-progress" aria-label="Event progress">
        {progressSteps.map((step, index) => (
          <div className={step.complete ? 'complete' : ''} key={step.label}>
            <span>{index + 1}</span>
            <strong>{step.label}</strong>
          </div>
        ))}
      </section>

      <nav className="detail-tabs" aria-label="Event detail sections">
        {Object.entries(tabTitles).map(([id, title]) => (
          <button
            type="button"
            className={tab === id ? 'active' : ''}
            onClick={() => setTab(id)}
            key={id}
          >
            {title}
          </button>
        ))}
      </nav>

      <div className="event-tab-print-content" ref={printContentRef} data-print-tab={tab}>
        {tab === 'assignments' && <ProductionAssignmentBoard />}
        {tab === 'overview' && (
          <div className="summary-grid">
            <section className="panel summary-section wide">
              <div className="summary-title">
                <div><span className="number">01</span><h2>Event details</h2></div>
                {!isWooEvent && (
                  <button type="button" className="icon-btn" aria-label="Edit event details" onClick={() => setShowEdit(true)}>
                    <SafeIcon icon={FiEdit3} />
                  </button>
                )}
              </div>
              {isWooEvent && <p className="read-only-note">From WooCommerce order #{event.wooOrderNumber}. Edit booking details in WooCommerce.</p>}
              <DetailTable rows={eventRows} />
              <DetailTable rows={logisticsRows} />
            </section>

            {services.length > 0 && (
              <section className="panel summary-section">
                <div className="summary-title"><div><span className="number">02</span><h2>Services booked</h2></div></div>
                <DetailTable rows={[
                  ['Services', services.join(', ')],
                  ['Details and specifications', event.serviceDetails]
                ]} />
              </section>
            )}
            {event.serviceDetails && (
              <section className="panel summary-section">
                <div className="summary-title"><div><span className="number">03</span><h2>Service specifications</h2></div></div>
                <p className="ai-summary">{event.serviceDetails}</p>
              </section>
            )}
            <VenueLogisticsAlerts event={event} />
            <ClientAnswersPanel event={event} />
            <EventActivityTimeline activities={activities} />
          </div>
        )}
        {tab === 'production' && (
          <section className="panel summary-section">
            <div className="summary-title"><div><span className="number">01</span><h2>DJ Production Sheet</h2></div></div>
            <DetailTable rows={[
              ['Event type', event.type],
              ['Assigned DJ', event.dj],
              ['Booked services', services.join(', ')],
              ['Guest count', event.guests],
              ['Venue', event.venue],
              ['Event date', formatEventDate(event.date)],
              ['Event duration', event.eventDuration],
              ['Event start time', event.eventStartTime],
              ['Setup start time', event.setupStartTime],
              ['Event end time', event.eventEndTime],
              ['Client email', event.email],
              ['Client phone', event.phone],
              ['Service specifications', event.serviceDetails]
            ]} />
            <ClientAnswersPanel event={event} />
          </section>
        )}
        {tab === 'notes' && (
          <section className="panel private-notes">
            <span className="private-label"><SafeIcon icon={FiCheckCircle} /> Internal production notes · Never visible to client</span>
            <h2>Team notes</h2>
            <textarea value={notes} onChange={(change) => setNotes(change.target.value)} placeholder="Add internal production notes..." />
            <div className="print-only-notes">{notes.trim() || 'No internal notes have been entered.'}</div>
            <button type="button" className="button primary" onClick={saveNotes}>Save internal notes</button>
          </section>
        )}
      </div>

      <div className="export-bar">
        <span><strong>Export current tab</strong><small>Print or save the open event section as a PDF.</small></span>
        <button type="button" className="button secondary" onClick={printCurrentTab}><SafeIcon icon={FiPrinter} /> Print</button>
        <button type="button" className="button primary" onClick={printCurrentTab}><SafeIcon icon={FiDownload} /> Generate PDF</button>
      </div>

      {showEdit && <EventEditModal event={event} onClose={() => setShowEdit(false)} onSave={saveEvent} />}
    </main>
  );
}

export default EventDetail;